/**
 * S9 — concept list for the ontology browser master pane.
 */

import React from 'react';
import { useLanguage } from '@/state/language/LanguageProvider';
import type { AiOntologyConcept } from '@/types/aiStudioAgent';

interface ConceptListProps {
  concepts: AiOntologyConcept[];
  selectedId: string | null;
  onSelect: (conceptId: string) => void;
}

export const ConceptList: React.FC<ConceptListProps> = ({
  concepts,
  selectedId,
  onSelect,
}) => {
  const { t } = useLanguage();

  if (concepts.length === 0) {
    return (
      <p className="px-3 py-4 text-base text-[var(--color-surface-text-secondary)]">
        {t('aiStudio.ontology.empty')}
      </p>
    );
  }

  return (
    <ul className="divide-y divide-[var(--color-border)]" role="listbox" aria-label={t('aiStudio.ontology.concepts')}>
      {concepts.map((concept) => {
        const selected = concept.id === selectedId;
        return (
          <li key={concept.id}>
            <button
              type="button"
              role="option"
              aria-selected={selected}
              onClick={() => onSelect(concept.id)}
              className={`flex w-full flex-col items-start gap-0.5 px-3 py-2.5 text-left focus:outline-none focus:ring-2 focus:ring-inset focus:ring-[var(--color-info)] ${
                selected
                  ? 'bg-[var(--color-secondary-bg)]'
                  : 'hover:bg-[var(--color-surface-hover)]'
              }`}
            >
              <span className="font-medium text-[var(--color-surface-text)]">
                {concept.code}
              </span>
              <span className="text-base text-[var(--color-surface-text-secondary)]">
                {concept.name}
              </span>
            </button>
          </li>
        );
      })}
    </ul>
  );
};
